import { LocalContext } from "context/AuthenticationContext";
import { ContextType } from "react";
import { from, Observable, skip, Subscription, switchMap } from "rxjs";

interface SkipInitialGuard {
  isLatest: () => boolean;
  runIfLatest: (callback: () => void) => void;
}

export abstract class BaseViewModel<
  Input extends Disposable,
  Output extends Disposable
> implements Disposable
{
  input: Input;
  output: Output;
  context?: ContextType<typeof LocalContext>;

  subscriptions: Subscription = new Subscription();

  constructor(input: Input, output: Output) {
    this.input = input;
    this.output = output;
    this.transform();
  }

  protected skipFirstInitial<T>(
    observable: Observable<T>,
    next: (value: T, guard?: SkipInitialGuard) => void | Promise<void>,
  ): void {
    let version = 0;
    const sub = observable
      .pipe(
        skip(1),
        switchMap((value) => {
          const currentVersion = ++version;
          const guard: SkipInitialGuard = {
            isLatest: () => currentVersion === version,
            runIfLatest: (callback: () => void) => {
              if (currentVersion === version) {
                callback();
              }
            },
          };

          return from(Promise.resolve(next(value, guard)));
        }),
      )
      .subscribe();
    this.subscriptions.add(sub);
  }

  protected transform(): void {}

  [Symbol.dispose](): void {
    this.input[Symbol.dispose]();
    this.output[Symbol.dispose]();
    this.subscriptions.unsubscribe();
  }
}
